import { collection, addDoc, getDocs, query, orderBy, limit, serverTimestamp, updateDoc, deleteDoc, doc, where } from 'firebase/firestore'
import { db } from '../firebase'
import { sanitizeHtml, trackEvent } from '../utils/helpers'

/**
 * REVIEWS SERVICE
 */
export const submitReview = async (reviewData) => {
  const sanitizedName = sanitizeHtml(reviewData.name)
  const sanitizedRole = sanitizeHtml(reviewData.role)
  const sanitizedText = sanitizeHtml(reviewData.text)

  const newReview = {
    name: sanitizedName,
    role: sanitizedRole,
    stars: reviewData.stars,
    text: sanitizedText,
    init: sanitizedName ? sanitizedName.substring(0, 2).toUpperCase() : 'U',
    approved: false,
    createdAt: serverTimestamp()
  }

  const docRef = await addDoc(collection(db, 'reviews'), newReview)
  trackEvent('review_submitted', { name: sanitizedName, stars: reviewData.stars })
  return docRef
}

export const getAdminReviews = async () => {
  // Uses getDocs instead of onSnapshot for stability as requested
  const q = query(collection(db, 'reviews'), orderBy('createdAt', 'desc'), limit(20))
  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
}

export const toggleReviewApproval = async (id, currentStatus) => {
  const reviewRef = doc(db, 'reviews', id)
  await updateDoc(reviewRef, { approved: !currentStatus })
  trackEvent('review_approval_toggled', { id, newStatus: !currentStatus })
}

export const deleteReview = async (id) => {
  await deleteDoc(doc(db, 'reviews', id))
  trackEvent('review_deleted', { id })
}

/**
 * LEADS SERVICE
 */
export const submitLead = async (leadData) => {
  const newLead = {
    name: sanitizeHtml(leadData.name),
    phone: sanitizeHtml(leadData.phone),
    email: sanitizeHtml(leadData.email),
    service: sanitizeHtml(leadData.service),
    message: sanitizeHtml(leadData.message),
    createdAt: serverTimestamp()
  }

  const docRef = await addDoc(collection(db, 'leads'), newLead)
  trackEvent('lead_submitted', { service: newLead.service })
  return docRef
}

export const getAdminLeads = async () => {
  const q = query(collection(db, 'leads'), orderBy('createdAt', 'desc'), limit(20))
  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
}

export const deleteLead = async (id) => {
  await deleteDoc(doc(db, 'leads', id))
  trackEvent('lead_deleted', { id })
}

/**
 * PROJECTS SERVICE
 */

// Define max lengths to match UI validation
const MAX_PROJECT_DESC = 300
const MAX_UPDATE_CONTENT = 500

export const addProject = async (projectData) => {
  try {
    // Basic duplication check (prevent double click within same session)
    // Querying only by title and filtering isDeleted locally avoids composite index
    const q = query(collection(db, 'projects'), where('title', '==', sanitizeHtml(projectData.title)), limit(5))
    const existingSnap = await getDocs(q)
    const existing = existingSnap.docs.map(doc => doc.data()).filter(d => !d.isDeleted)
    if (existing.length > 0) {
      throw new Error(`A project with title "${projectData.title}" already exists.`)
    }

    const newProject = {
      title: sanitizeHtml(projectData.title),
      category: sanitizeHtml(projectData.category),
      image: sanitizeHtml(projectData.image),
      location: sanitizeHtml(projectData.location),
      description: sanitizeHtml(projectData.description).substring(0, MAX_PROJECT_DESC),
      featured: Boolean(projectData.featured),
      status: sanitizeHtml(projectData.status) || 'completed',
      isDeleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }
    const docRef = await addDoc(collection(db, 'projects'), newProject)
    trackEvent('project_added', { title: newProject.title })
    return docRef.id
  } catch (error) {
    console.error("Error adding project:", error)
    throw error
  }
}

export const updateProject = async (id, projectData) => {
  try {
    const projectRef = doc(db, 'projects', id)
    const updates = {
      title: sanitizeHtml(projectData.title),
      category: sanitizeHtml(projectData.category),
      image: sanitizeHtml(projectData.image),
      location: sanitizeHtml(projectData.location),
      description: sanitizeHtml(projectData.description).substring(0, MAX_PROJECT_DESC),
      featured: Boolean(projectData.featured),
      status: sanitizeHtml(projectData.status) || 'completed',
      updatedAt: serverTimestamp()
    }
    await updateDoc(projectRef, updates)
    trackEvent('project_updated', { id })
  } catch (error) {
    console.error("Error updating project:", error)
    throw error
  }
}

export const deleteProject = async (id, hardDelete = false) => {
  try {
    if (hardDelete) {
      await deleteDoc(doc(db, 'projects', id))
      trackEvent('project_hard_deleted', { id })
    } else {
      const projectRef = doc(db, 'projects', id)
      await updateDoc(projectRef, { isDeleted: true, updatedAt: serverTimestamp() })
      trackEvent('project_soft_deleted', { id })
    }
  } catch (error) {
    console.error("Error deleting project:", error)
    throw error
  }
}

export const restoreProject = async (id) => {
  try {
    const projectRef = doc(db, 'projects', id)
    await updateDoc(projectRef, { isDeleted: false, updatedAt: serverTimestamp() })
    trackEvent('project_restored', { id })
  } catch (error) {
    console.error("Error restoring project:", error)
    throw error
  }
}

export const getAdminProjects = async () => {
  try {
    const q = query(collection(db, 'projects'), orderBy('createdAt', 'desc'), limit(50))
    const snapshot = await getDocs(q)
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
  } catch (error) {
    console.error("Error getting admin projects:", error)
    throw error
  }
}

export const getPublicProjects = async () => {
  try {
    const q = query(collection(db, 'projects'), where('isDeleted', '==', false))
    const snapshot = await getDocs(q)
    let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    // Sort client-side to avoid composite index requirement
    results.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    return results.slice(0, 20)
  } catch (error) {
    console.error("Error getting public projects:", error)
    throw error
  }
}

export const getFeaturedProjects = async () => {
  try {
    const q = query(collection(db, 'projects'), where('isDeleted', '==', false))
    const snapshot = await getDocs(q)
    let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    // Filter and sort client-side
    let featured = results.filter(p => p.featured)
    featured.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    return featured.slice(0, 10)
  } catch (error) {
    console.error("Error getting featured projects:", error)
    throw error
  }
}

/**
 * PROPERTIES SERVICE
 */

export const addProperty = async (propertyData) => {
  try {
    const q = query(collection(db, 'properties'), where('title', '==', sanitizeHtml(propertyData.title)), limit(5))
    const existingSnap = await getDocs(q)
    const existing = existingSnap.docs.map(doc => doc.data()).filter(d => !d.isDeleted)
    if (existing.length > 0) {
      throw new Error(`A property with title "${propertyData.title}" already exists.`)
    }

    const newProperty = {
      title: sanitizeHtml(propertyData.title),
      type: sanitizeHtml(propertyData.type),
      badge: sanitizeHtml(propertyData.badge),
      img: sanitizeHtml(propertyData.img),
      loc: sanitizeHtml(propertyData.loc),
      area: sanitizeHtml(propertyData.area),
      legal: sanitizeHtml(propertyData.legal),
      isDeleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }
    const docRef = await addDoc(collection(db, 'properties'), newProperty)
    trackEvent('property_added', { title: newProperty.title })
    return docRef.id
  } catch (error) {
    console.error("Error adding property:", error)
    throw error
  }
}

export const updateProperty = async (id, propertyData) => {
  try {
    const propertyRef = doc(db, 'properties', id)
    const updates = {
      title: sanitizeHtml(propertyData.title),
      type: sanitizeHtml(propertyData.type),
      badge: sanitizeHtml(propertyData.badge),
      img: sanitizeHtml(propertyData.img),
      loc: sanitizeHtml(propertyData.loc),
      area: sanitizeHtml(propertyData.area),
      legal: sanitizeHtml(propertyData.legal),
      updatedAt: serverTimestamp()
    }
    await updateDoc(propertyRef, updates)
    trackEvent('property_updated', { id })
  } catch (error) {
    console.error("Error updating property:", error)
    throw error
  }
}

export const deleteProperty = async (id, hardDelete = false) => {
  try {
    if (hardDelete) {
      await deleteDoc(doc(db, 'properties', id))
      trackEvent('property_hard_deleted', { id })
    } else {
      const propertyRef = doc(db, 'properties', id)
      await updateDoc(propertyRef, { isDeleted: true, updatedAt: serverTimestamp() })
      trackEvent('property_soft_deleted', { id })
    }
  } catch (error) {
    console.error("Error deleting property:", error)
    throw error
  }
}

export const restoreProperty = async (id) => {
  try {
    const propertyRef = doc(db, 'properties', id)
    await updateDoc(propertyRef, { isDeleted: false, updatedAt: serverTimestamp() })
    trackEvent('property_restored', { id })
  } catch (error) {
    console.error("Error restoring property:", error)
    throw error
  }
}

export const getAdminProperties = async () => {
  try {
    const q = query(collection(db, 'properties'), orderBy('createdAt', 'desc'), limit(50))
    const snapshot = await getDocs(q)
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
  } catch (error) {
    console.error("Error getting admin properties:", error)
    throw error
  }
}

export const getPublicProperties = async () => {
  try {
    const q = query(collection(db, 'properties'), where('isDeleted', '==', false))
    const snapshot = await getDocs(q)
    let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    // Sort client-side to avoid composite index requirement
    results.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    return results.slice(0, 20)
  } catch (error) {
    console.error("Error getting public properties:", error)
    throw error
  }
}

/**
 * UPDATES SERVICE
 */

const generateSlug = (title) => {
  const baseSlug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '')
  const randomSuffix = Math.random().toString(36).substring(2, 6)
  return `${baseSlug}-${randomSuffix}`
}

export const addUpdate = async (updateData) => {
  try {
    // Querying only by title and filtering isDeleted locally avoids composite index
    const q = query(collection(db, 'updates'), where('title', '==', sanitizeHtml(updateData.title)), limit(5))
    const existingSnap = await getDocs(q)
    const existing = existingSnap.docs.map(doc => doc.data()).filter(d => !d.isDeleted)
    if (existing.length > 0) {
      throw new Error(`An update with title "${updateData.title}" already exists.`)
    }

    const newUpdate = {
      title: sanitizeHtml(updateData.title),
      image: sanitizeHtml(updateData.image),
      content: sanitizeHtml(updateData.content).substring(0, MAX_UPDATE_CONTENT),
      slug: updateData.slug ? sanitizeHtml(updateData.slug) : generateSlug(updateData.title),
      isDeleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }
    const docRef = await addDoc(collection(db, 'updates'), newUpdate)
    trackEvent('update_added', { title: newUpdate.title })
    return docRef.id
  } catch (error) {
    console.error("Error adding update:", error)
    throw error
  }
}

export const updateUpdate = async (id, updateData) => {
  try {
    const updateRef = doc(db, 'updates', id)
    const updates = {
      title: sanitizeHtml(updateData.title),
      image: sanitizeHtml(updateData.image),
      content: sanitizeHtml(updateData.content).substring(0, MAX_UPDATE_CONTENT),
      slug: sanitizeHtml(updateData.slug),
      updatedAt: serverTimestamp()
    }
    await updateDoc(updateRef, updates)
    trackEvent('update_updated', { id })
  } catch (error) {
    console.error("Error editing update:", error)
    throw error
  }
}

export const deleteUpdate = async (id, hardDelete = false) => {
  try {
    if (hardDelete) {
      await deleteDoc(doc(db, 'updates', id))
      trackEvent('update_hard_deleted', { id })
    } else {
      const updateRef = doc(db, 'updates', id)
      await updateDoc(updateRef, { isDeleted: true, updatedAt: serverTimestamp() })
      trackEvent('update_soft_deleted', { id })
    }
  } catch (error) {
    console.error("Error deleting update:", error)
    throw error
  }
}

export const restoreUpdate = async (id) => {
  try {
    const updateRef = doc(db, 'updates', id)
    await updateDoc(updateRef, { isDeleted: false, updatedAt: serverTimestamp() })
    trackEvent('update_restored', { id })
  } catch (error) {
    console.error("Error restoring update:", error)
    throw error
  }
}

export const getAdminUpdates = async () => {
  try {
    const q = query(collection(db, 'updates'), orderBy('createdAt', 'desc'), limit(50))
    const snapshot = await getDocs(q)
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
  } catch (error) {
    console.error("Error getting admin updates:", error)
    throw error
  }
}

export const getPublicUpdates = async () => {
  try {
    const q = query(collection(db, 'updates'), where('isDeleted', '==', false))
    const snapshot = await getDocs(q)
    let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    results.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    return results.slice(0, 10)
  } catch (error) {
    console.error("Error getting public updates:", error)
    throw error
  }
}

/**
 * SERVICES SERVICE
 */

const generateServiceSlug = (title) => {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '') + '-delhi-ncr'
}

export const addService = async (serviceData) => {
  try {
    const q = query(collection(db, 'services'), where('title', '==', sanitizeHtml(serviceData.title)), limit(5))
    const existingSnap = await getDocs(q)
    const existing = existingSnap.docs.map(doc => doc.data()).filter(d => !d.isDeleted)
    if (existing.length > 0) {
      throw new Error(`A service with title "${serviceData.title}" already exists.`)
    }

    const newService = {
      title: sanitizeHtml(serviceData.title),
      icon: sanitizeHtml(serviceData.icon) || 'fas fa-hard-hat',
      shortDescription: sanitizeHtml(serviceData.shortDescription),
      fullDescription: sanitizeHtml(serviceData.fullDescription).substring(0, 2000),
      image: sanitizeHtml(serviceData.image),
      slug: serviceData.slug ? sanitizeHtml(serviceData.slug) : generateServiceSlug(serviceData.title),
      featured: Boolean(serviceData.featured),
      linkOverride: serviceData.linkOverride ? sanitizeHtml(serviceData.linkOverride) : '',
      benefits: Array.isArray(serviceData.benefits) ? serviceData.benefits.map(sanitizeHtml) : [],
      locations: Array.isArray(serviceData.locations) ? serviceData.locations.map(sanitizeHtml) : [],
      process: Array.isArray(serviceData.process) ? serviceData.process.map(p => ({
        step: sanitizeHtml(p.step),
        desc: sanitizeHtml(p.desc)
      })) : [],
      faqs: Array.isArray(serviceData.faqs) ? serviceData.faqs.map(f => ({
        q: sanitizeHtml(f.q),
        a: sanitizeHtml(f.a)
      })) : [],
      isDeleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }
    const docRef = await addDoc(collection(db, 'services'), newService)
    trackEvent('service_added', { title: newService.title })
    return docRef.id
  } catch (error) {
    console.error("Error adding service:", error)
    throw error
  }
}

export const updateService = async (id, serviceData) => {
  try {
    const serviceRef = doc(db, 'services', id)
    const updates = {
      title: sanitizeHtml(serviceData.title),
      icon: sanitizeHtml(serviceData.icon) || 'fas fa-hard-hat',
      shortDescription: sanitizeHtml(serviceData.shortDescription),
      fullDescription: sanitizeHtml(serviceData.fullDescription).substring(0, 2000),
      image: sanitizeHtml(serviceData.image),
      slug: sanitizeHtml(serviceData.slug),
      featured: Boolean(serviceData.featured),
      linkOverride: serviceData.linkOverride ? sanitizeHtml(serviceData.linkOverride) : '',
      benefits: Array.isArray(serviceData.benefits) ? serviceData.benefits.map(sanitizeHtml) : [],
      locations: Array.isArray(serviceData.locations) ? serviceData.locations.map(sanitizeHtml) : [],
      process: Array.isArray(serviceData.process) ? serviceData.process.map(p => ({
        step: sanitizeHtml(p.step),
        desc: sanitizeHtml(p.desc)
      })) : [],
      faqs: Array.isArray(serviceData.faqs) ? serviceData.faqs.map(f => ({
        q: sanitizeHtml(f.q),
        a: sanitizeHtml(f.a)
      })) : [],
      updatedAt: serverTimestamp()
    }
    await updateDoc(serviceRef, updates)
    trackEvent('service_updated', { id })
  } catch (error) {
    console.error("Error updating service:", error)
    throw error
  }
}

export const deleteService = async (id, hardDelete = false) => {
  try {
    if (hardDelete) {
      await deleteDoc(doc(db, 'services', id))
      trackEvent('service_hard_deleted', { id })
    } else {
      const serviceRef = doc(db, 'services', id)
      await updateDoc(serviceRef, { isDeleted: true, updatedAt: serverTimestamp() })
      trackEvent('service_soft_deleted', { id })
    }
  } catch (error) {
    console.error("Error deleting service:", error)
    throw error
  }
}

export const restoreService = async (id) => {
  try {
    const serviceRef = doc(db, 'services', id)
    await updateDoc(serviceRef, { isDeleted: false, updatedAt: serverTimestamp() })
    trackEvent('service_restored', { id })
  } catch (error) {
    console.error("Error restoring service:", error)
    throw error
  }
}

export const getAdminServices = async () => {
  try {
    const q = query(collection(db, 'services'), orderBy('createdAt', 'desc'), limit(50))
    const snapshot = await getDocs(q)
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
  } catch (error) {
    console.error("Error getting admin services:", error)
    throw error
  }
}

export const getPublicServices = async () => {
  try {
    const q = query(collection(db, 'services'), where('isDeleted', '==', false))
    const snapshot = await getDocs(q)
    let results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    results.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    return results
  } catch (error) {
    console.error("Error getting public services:", error)
    throw error
  }
}

